import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus, Search, Edit, Trash2, User, Phone, Mail, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientList = ({ agent, onNavigate, onSelectClient, isAdmin = false }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // Iter 35: when admin opens this page they can pick an agent to act on
  // behalf of — new clients are created under that agent's id, and the
  // visible client list narrows to that agent.
  const adminMode = isAdmin || agent?.id === 'admin';
  const [allAgents, setAllAgents] = useState([]);
  const [onBehalfAgentId, setOnBehalfAgentId] = useState('');
  const onBehalfAgent = adminMode ? allAgents.find(a => a.id === onBehalfAgentId) : null;
  // Effective agent for create/update advisor_id query params.
  const effectiveAgent = adminMode ? onBehalfAgent : agent;

  // Load roster once, only for admin
  useEffect(() => {
    if (!adminMode) return;
    fetch(`${API_URL}/api/agents`)
      .then(r => r.ok ? r.json() : [])
      .then(setAllAgents)
      .catch(err => console.error('Failed to fetch agents roster:', err));
  }, [adminMode]);

  const fetchClients = useCallback(async () => {
    try {
      // Iter 35: admin narrows the list to the picked on-behalf agent
      let url;
      if (adminMode && onBehalfAgent?.id) {
        url = `${API_URL}/api/compliance/clients?advisor_id=${onBehalfAgent.id}`;
      } else if (adminMode) {
        url = `${API_URL}/api/compliance/clients`;
      } else {
        url = `${API_URL}/api/compliance/clients?advisor_id=${agent.id}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  }, [agent, adminMode, onBehalfAgent]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Iter 35: refetch when admin changes the on-behalf agent
  useEffect(() => {
    if (adminMode) fetchClients();
     
  }, [onBehalfAgentId]);

  const handleSaveClient = async (clientData) => {
    // Iter 35: admin must pick agent before creating a client
    if (adminMode && !effectiveAgent?.id) {
      alert('Pick the agent you are creating this client for (top of page).');
      return;
    }
    try {
      const advisorId = effectiveAgent.id;
      const advisorName = encodeURIComponent(effectiveAgent.name);
      const url = editingClient
        ? `${API_URL}/api/compliance/clients/${editingClient.id}?advisor_id=${advisorId}&advisor_name=${advisorName}`
        : `${API_URL}/api/compliance/clients?advisor_id=${advisorId}&advisor_name=${advisorName}`;
      
      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientData)
      });

      if (response.ok) {
        fetchClients();
        setShowModal(false);
        setEditingClient(null);
      }
    } catch (err) {
      console.error('Failed to save client:', err);
    }
  };

  const handleDeleteClient = async (client, cascade = false) => {
    const advisorId = (isAdmin || agent?.id === 'admin') ? 'admin' : agent.id;
    const advisorName = encodeURIComponent(agent?.name || 'admin');
    const url = `${API_URL}/api/compliance/clients/${client.id}?advisor_id=${advisorId}&advisor_name=${advisorName}${cascade ? '&cascade=true' : ''}`;

    try {
      const response = await fetch(url, { method: 'DELETE' });
      // Read raw text first so we never lose the body to a transient JSON parse glitch
      const raw = await response.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }

      if (response.status === 409) {
        // FastAPI HTTPException(detail=dict) → body is {detail:{message, linked_cases}}.
        // FastAPI HTTPException(detail=str)  → body is {detail: "string"}.
        // Be defensive against both shapes.
        const det = data?.detail;
        const linked = (det && typeof det === 'object' && Array.isArray(det.linked_cases))
          ? det.linked_cases
          : [];
        const msg = (det && typeof det === 'object' && det.message)
          ? det.message
          : (typeof det === 'string' ? det : 'Client has linked compliance files.');

        const ok = window.confirm(
          `${msg}\n\n` +
          (linked.length
            ? `Linked files (${linked.length}): ${linked.slice(0, 10).join(', ')}${linked.length > 10 ? `, …+${linked.length - 10} more` : ''}\n\n`
            : '') +
          `Deleting the client will PERMANENTLY DELETE every linked file too. This cannot be undone.\n\n` +
          `Click OK to delete client + all file(s), or Cancel to keep everything.`
        );
        if (ok) {
          await handleDeleteClient(client, true);
        }
        return;
      }

      if (!response.ok) {
        const detailMsg = (data?.detail && typeof data.detail === 'string')
          ? data.detail
          : (data?.detail?.message || response.statusText);
        alert(`Failed to delete client: ${detailMsg}`);
        return;
      }

      const cascaded = data.cases_cascade_deleted?.length || 0;
      alert(
        `Client "${data.client_name}" was permanently deleted` +
        (cascaded ? ` along with ${cascaded} compliance file(s).` : '.') +
        (isAdmin || agent?.id === 'admin' ? '' : '\n\nA confirmation email has been sent to your inbox.')
      );
      fetchClients();
    } catch (err) {
      console.error('Failed to delete client:', err);
      alert(`Network error: ${err.message}`);
    }
  };

  const confirmDelete = (client) => {
    const ok = window.confirm(
      `Permanently delete client "${client.full_name}" (${client.email || 'no email'})?\n\n` +
      `This cannot be undone. If the client has any compliance files, you will be asked again before those are deleted.`
    );
    if (ok) handleDeleteClient(client, false);
  };

  const filteredClients = clients.filter(client =>
    client.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003D5C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Iter 35: admin acts on behalf of a specific agent */}
      {adminMode && (
        <Card className="bg-amber-50 border-amber-300" data-testid="clientlist-admin-on-behalf-card">
          <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-semibold text-amber-900 whitespace-nowrap">
              Acting on behalf of:
            </label>
            <select
              value={onBehalfAgentId}
              onChange={(e) => setOnBehalfAgentId(e.target.value)}
              className="flex-1 px-3 py-1.5 border rounded text-sm bg-white"
              data-testid="clientlist-on-behalf-agent-select"
            >
              <option value="">— All agents (read-only) —</option>
              {allAgents
                .slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.license_number ? ` · LIC ${a.license_number}` : ''}
                  </option>
                ))}
            </select>
            <span className="text-xs text-amber-800">
              Pick an agent to add or edit clients under their account.
            </span>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#003D5C]">Clients</h1>
          <p className="text-sm text-gray-500">
            {adminMode && !onBehalfAgent ? 'Browsing all clients across every agent' : 'Manage your compliance clients'}
          </p>
        </div>
        <Button
          onClick={() => { setEditingClient(null); setShowModal(true); }}
          disabled={adminMode && !onBehalfAgent}
          className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
          title={adminMode && !onBehalfAgent ? 'Pick an agent first' : ''}
          data-testid="clientlist-add-client-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search clients by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
        />
      </div>

      {/* Client List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.map(client => (
          <Card
            key={client.id}
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => onNavigate('client-folder', { client })}
            data-testid={`client-folder-open-${client.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#003D5C] to-[#0077B6] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {client.full_name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{client.full_name}</h3>
                    <p className="text-sm text-gray-500">{client.occupation || 'No occupation'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setEditingClient(client); setShowModal(true); }}
                    data-testid={`edit-client-${client.id}`}
                    title="Edit client"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); confirmDelete(client); }}
                    data-testid={`delete-client-${client.id}`}
                    title="Permanently delete client"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{client.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{client.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>{client.city}, {client.province}</span>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={(e) => { e.stopPropagation(); onSelectClient && onSelectClient(client); }}
                >
                  Select for Case
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 bg-[#0077B6] hover:bg-[#005A8C]"
                  onClick={(e) => { e.stopPropagation(); onNavigate('new-case', { clientId: client.id }); }}
                >
                  New File
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredClients.length === 0 && (
          <div className="col-span-3 text-center py-12">
            <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No clients found</p>
            <Button 
              onClick={() => setShowModal(true)}
              className="mt-4 bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]"
            >
              Add Your First Client
            </Button>
          </div>
        )}
      </div>

      {/* Client Modal */}
      {showModal && (
        <ClientModal
          client={editingClient}
          onSave={handleSaveClient}
          onClose={() => { setShowModal(false); setEditingClient(null); }}
          agent={agent}
          onBehalfAgent={onBehalfAgent}
          apiUrl={API_URL}
          onSentIntake={() => { setShowModal(false); setEditingClient(null); fetchClients && fetchClients(); }}
        />
      )}
    </div>
  );
};

// Client Modal Component
const ClientModal = ({ client, onSave, onClose, agent, onBehalfAgent, apiUrl, onSentIntake }) => {
  // Iter 48 — Self-Intake link state: agent can email a public form link to
  // the client to fill this whole page themselves; a stub case is created
  // immediately so it appears in the agent's case list as 'intake_pending'.
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkName, setLinkName] = useState('');
  const [linkPhone, setLinkPhone] = useState('');
  const [linkSending, setLinkSending] = useState(false);
  const [linkResult, setLinkResult] = useState(null);  // { intake_url, case_number, email_status } or { error }

  const sendIntakeLink = async () => {
    setLinkResult(null);
    if (!linkName.trim()) { setLinkResult({ error: 'Client name is required.' }); return; }
    if (!linkEmail.trim() || !linkEmail.includes('@')) {
      setLinkResult({ error: 'A valid client email is required.' });
      return;
    }
    const advisorId = onBehalfAgent?.id || agent?.id;
    const advisorName = onBehalfAgent?.name || agent?.name || 'Advisor';
    if (!advisorId || advisorId === 'admin') {
      setLinkResult({ error: 'Please pick a specific agent first (Admin-on-behalf) before sending an intake link.' });
      return;
    }
    setLinkSending(true);
    try {
      const url = `${apiUrl}/api/compliance/pre-case-intake-requests?advisor_id=${encodeURIComponent(advisorId)}&advisor_name=${encodeURIComponent(advisorName)}`;
      const r = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: linkName.trim(),
          client_email: linkEmail.trim().toLowerCase(),
          client_phone: linkPhone.trim() || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Failed (${r.status})`);
      }
      const data = await r.json();
      setLinkResult({
        intake_url: data.intake_url,
        case_number: data.case?.case_number,
        email_status: data.intake_request?.email_status,
      });
    } catch (e) {
      setLinkResult({ error: e.message || 'Failed to send intake link' });
    } finally {
      setLinkSending(false);
    }
  };

  const [formData, setFormData] = useState({
    full_name: client?.full_name || '',
    preferred_name: client?.preferred_name || '',
    date_of_birth: client?.date_of_birth || '',
    gender: client?.gender || '',
    marital_status: client?.marital_status || '',
    dependents: client?.dependents || 0,
    dependents_list: client?.dependents_list || [],
    address: client?.address || '',
    city: client?.city || '',
    province: client?.province || 'ON',
    postal_code: client?.postal_code || '',
    phone: client?.phone || '',
    email: client?.email || '',
    preferred_contact_method: client?.preferred_contact_method || 'email',
    occupation: client?.occupation || '',
    employer: client?.employer || '',
    employment_type: client?.employment_type || '',
    years_employed: client?.years_employed || '',
    annual_income: client?.annual_income || '',
    smoker_status: client?.smoker_status || 'non-smoker',
    residency_status: client?.residency_status || 'Canadian Citizen',
    sin: client?.sin || '',
    // Iter 31: explicit Yes/No that gates the spouse section regardless of marital_status.
    // Auto-true if there's a spouse on file (back-compat) OR client is married/common-law.
    include_other_insureds: client?.include_other_insureds
      || !!client?.spouse_name
      || ['married', 'common-law'].includes(client?.marital_status || ''),
    spouse_name: client?.spouse_name || '',
    spouse_income: client?.spouse_income || '',
    spouse_date_of_birth: client?.spouse_date_of_birth || '',
    spouse_gender: client?.spouse_gender || '',
    spouse_occupation: client?.spouse_occupation || '',
    spouse_employer: client?.spouse_employer || '',
    spouse_smoker_status: client?.spouse_smoker_status || 'non-smoker',
    spouse_phone: client?.spouse_phone || '',
    spouse_email: client?.spouse_email || '',
    spouse_address: client?.spouse_address || '',
    spouse_sin: client?.spouse_sin || ''
  });

  const [saving, setSaving] = useState(false);

  const addDependent = () => {
    setFormData({
      ...formData,
      dependents_list: [...formData.dependents_list, { _key: `dep-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, name: '', relationship: 'son', date_of_birth: '', financially_dependent: true }],
      dependents: formData.dependents_list.length + 1,
    });
  };

  const updateDependent = (idx, field, value) => {
    const updated = [...formData.dependents_list];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormData({ ...formData, dependents_list: updated });
  };

  const removeDependent = (idx) => {
    const updated = formData.dependents_list.filter((_, i) => i !== idx);
    setFormData({ ...formData, dependents_list: updated, dependents: updated.length });
  };

  const provinces = [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'YT', label: 'Yukon' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      ...formData,
      dependents: parseInt(formData.dependents) || 0,
      years_employed: parseInt(formData.years_employed) || 0,
      annual_income: parseFloat(formData.annual_income) || 0,
      spouse_income: parseFloat(formData.spouse_income) || 0
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50 sm:p-4">
      <Card className="w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-lg">
        <CardHeader className="sticky top-0 bg-white z-10 border-b">
          <div className="flex justify-between items-center">
            <CardTitle>{client ? 'Edit Client' : 'New Client'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>×</Button>
          </div>
          {/* Iter 48 — Send the client a link to fill this page out themselves */}
          {!client && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              {!showLinkForm && !linkResult?.intake_url && (
                <button
                  type="button"
                  onClick={() => setShowLinkForm(true)}
                  className="text-sm font-medium text-amber-800 hover:text-amber-900 inline-flex items-center gap-2"
                  data-testid="send-intake-link-toggle"
                >
                  📩 <span>Send a self-intake link to the client</span>
                  <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded uppercase font-semibold">Saves time</span>
                </button>
              )}
              {showLinkForm && !linkResult?.intake_url && (
                <div className="space-y-2" data-testid="send-intake-link-form">
                  <p className="text-xs text-amber-900">
                    Send a secure link to your client. They will fill out this same New Client form on their own — when they submit, the new file appears in your Compliance Files list as <b>intake_pending</b> and ready for review.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input
                      type="text" placeholder="Client name *"
                      value={linkName} onChange={(e) => setLinkName(e.target.value)}
                      className="px-3 py-2 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400"
                      data-testid="intake-link-name"
                    />
                    <input
                      type="email" placeholder="Client email *"
                      value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)}
                      className="px-3 py-2 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400"
                      data-testid="intake-link-email"
                    />
                    <input
                      type="tel" placeholder="Phone (optional)"
                      value={linkPhone} onChange={(e) => setLinkPhone(e.target.value)}
                      className="px-3 py-2 text-sm border border-amber-300 rounded bg-white focus:ring-2 focus:ring-amber-400"
                      data-testid="intake-link-phone"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={sendIntakeLink}
                      disabled={linkSending}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="send-intake-link-btn"
                    >
                      {linkSending ? 'Sending…' : 'Send link'}
                    </Button>
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => { setShowLinkForm(false); setLinkResult(null); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {linkResult?.error && (
                <p className="text-xs text-red-700 mt-1" data-testid="intake-link-error">{linkResult.error}</p>
              )}
              {linkResult?.intake_url && (
                <div className="text-xs text-amber-900 space-y-1" data-testid="intake-link-success">
                  <p>
                    ✅ Stub file <b>{linkResult.case_number}</b> created.{' '}
                    Email status: <b>{linkResult.email_status}</b>.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={linkResult.intake_url}
                      className="flex-1 px-2 py-1 text-[11px] border border-amber-300 rounded bg-white font-mono"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => navigator.clipboard.writeText(linkResult.intake_url)}
                      className="text-xs"
                      data-testid="intake-link-copy"
                    >
                      Copy
                    </Button>
                    <Button
                      type="button" size="sm"
                      onClick={onSentIntake}
                      className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                    >
                      Close
                    </Button>
                  </div>
                  <p className="text-[11px] text-amber-700">
                    Share the link directly if your client did not receive the email (check spam).
                  </p>
                </div>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 pb-2 border-b">Personal Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Full Legal Name *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  >
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Marital Status</label>
                  <select
                    value={formData.marital_status}
                    onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  >
                    <option value="">Select...</option>
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="common-law">Common-Law</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Number of Dependents</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.dependents}
                    onChange={(e) => setFormData({ ...formData, dependents: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
              </div>
            </div>

            {/* Dependents Names */}
            <div>
              <div className="flex items-center justify-between mb-3 pb-2 border-b">
                <h3 className="font-semibold text-gray-900">Dependents</h3>
                <Button type="button" variant="outline" size="sm" onClick={addDependent} data-testid="add-dependent-btn">
                  <Plus className="h-4 w-4 mr-1" />Add Dependent
                </Button>
              </div>
              {formData.dependents_list.length === 0 ? (
                <p className="text-sm text-gray-500">No dependents added.</p>
              ) : (
                <div className="space-y-2">
                  {formData.dependents_list.map((dep, idx) => (
                    <div key={dep._key || `dep-fallback-${idx}`} className="grid md:grid-cols-12 gap-2 items-start p-2 border rounded-lg bg-gray-50">
                      <input
                        type="text"
                        value={dep.name}
                        onChange={(e) => updateDependent(idx, 'name', e.target.value)}
                        placeholder="Full name"
                        className="md:col-span-4 px-2 py-1.5 border rounded text-sm"
                        data-testid={`dep-name-${idx}`}
                      />
                      <select
                        value={dep.relationship || 'son'}
                        onChange={(e) => updateDependent(idx, 'relationship', e.target.value)}
                        className="md:col-span-3 px-2 py-1.5 border rounded text-sm"
                        data-testid={`dep-relationship-${idx}`}
                      >
                        <option value="son">Son</option>
                        <option value="daughter">Daughter</option>
                        <option value="spouse">Spouse</option>
                        <option value="parent">Parent</option>
                        <option value="sibling">Sibling</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="date"
                        value={dep.date_of_birth || ''}
                        onChange={(e) => updateDependent(idx, 'date_of_birth', e.target.value)}
                        className="md:col-span-3 px-2 py-1.5 border rounded text-sm"
                        data-testid={`dep-dob-${idx}`}
                      />
                      <label className="md:col-span-1 flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={dep.financially_dependent !== false}
                          onChange={(e) => updateDependent(idx, 'financially_dependent', e.target.checked)}
                          data-testid={`dep-fin-${idx}`}
                        />
                        Fin.Dep
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDependent(idx)}
                        className="md:col-span-1"
                        data-testid={`remove-dep-${idx}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 pb-2 border-b">Contact Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Province *</label>
                  <select
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  >
                    {provinces.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Contact Method</label>
                  <select
                    value={formData.preferred_contact_method}
                    onChange={(e) => setFormData({ ...formData, preferred_contact_method: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Employment */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 pb-2 border-b">Employment & Income</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Occupation</label>
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Employer</label>
                  <input
                    type="text"
                    value={formData.employer}
                    onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Annual Income ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.annual_income}
                    onChange={(e) => setFormData({ ...formData, annual_income: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Smoker Status</label>
                  <select
                    value={formData.smoker_status}
                    onChange={(e) => setFormData({ ...formData, smoker_status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  >
                    <option value="non-smoker">Non-Smoker</option>
                    <option value="smoker">Smoker</option>
                    <option value="former-smoker">Former Smoker</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Social Insurance Number (SIN)</label>
                  <input
                    type="text"
                    value={formData.sin}
                    onChange={(e) => setFormData({ ...formData, sin: e.target.value })}
                    placeholder="XXX-XXX-XXX"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="client-sin"
                  />
                </div>
              </div>
            </div>

            {/* Iter 31: explicit yes/no gate for spouse / other insureds */}
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData.include_other_insureds}
                  onChange={(e) => setFormData({ ...formData, include_other_insureds: e.target.checked })}
                  className="mt-1 w-4 h-4"
                  data-testid="client-include-other-insureds"
                />
                <span className="text-sm text-gray-800">
                  <b>Are you married, in a common-law relationship, or do you want other individuals on this application?</b>
                  <br />
                  <span className="text-xs text-gray-600">If checked, the spouse / partner information section below will be shown and used throughout the compliance workflow (fact-find, needs analysis, PDF).</span>
                </span>
              </label>
            </div>

            {/* Spouse Info — gated by the explicit checkbox above (Iter 31).
                Marital status is no longer the sole trigger — fixes the case where
                single clients want to add a partner / child / parent insured. */}
            {formData.include_other_insureds && (
              <div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 mb-3">
                  <b>Side-by-side spouse template enabled.</b> Fields entered here render side-by-side with the primary client on the Fact Findings, Needs Analysis, and final PDF compliance package.
                </div>
                <h3 className="font-semibold text-gray-900 mb-3 pb-2 border-b">Spouse / Partner Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Full Name</label>
                    <input
                      type="text"
                      value={formData.spouse_name}
                      onChange={(e) => setFormData({ ...formData, spouse_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="client-spouse-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Date of Birth</label>
                    <input
                      type="date"
                      value={formData.spouse_date_of_birth}
                      onChange={(e) => setFormData({ ...formData, spouse_date_of_birth: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="client-spouse-dob"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Gender</label>
                    <select
                      value={formData.spouse_gender}
                      onChange={(e) => setFormData({ ...formData, spouse_gender: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    >
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Smoker Status</label>
                    <select
                      value={formData.spouse_smoker_status}
                      onChange={(e) => setFormData({ ...formData, spouse_smoker_status: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    >
                      <option value="non-smoker">Non-Smoker</option>
                      <option value="smoker">Smoker</option>
                      <option value="former-smoker">Former Smoker</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Occupation</label>
                    <input
                      type="text"
                      value={formData.spouse_occupation}
                      onChange={(e) => setFormData({ ...formData, spouse_occupation: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Employer</label>
                    <input
                      type="text"
                      value={formData.spouse_employer}
                      onChange={(e) => setFormData({ ...formData, spouse_employer: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Annual Income ($)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.spouse_income}
                      onChange={(e) => setFormData({ ...formData, spouse_income: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Phone</label>
                    <input
                      type="tel"
                      value={formData.spouse_phone}
                      onChange={(e) => setFormData({ ...formData, spouse_phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Spouse Email</label>
                    <input
                      type="email"
                      value={formData.spouse_email}
                      onChange={(e) => setFormData({ ...formData, spouse_email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse SIN</label>
                    <input
                      type="text"
                      value={formData.spouse_sin}
                      onChange={(e) => setFormData({ ...formData, spouse_sin: e.target.value })}
                      placeholder="XXX-XXX-XXX"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="client-spouse-sin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Spouse Location / Address</label>
                    <input
                      type="text"
                      value={formData.spouse_address}
                      onChange={(e) => setFormData({ ...formData, spouse_address: e.target.value })}
                      placeholder="If different from household address"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      data-testid="client-spouse-address"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="flex-1 bg-[#0077B6] hover:bg-[#005A8C]"
              >
                {saving ? 'Saving...' : (client ? 'Update Client' : 'Create Client')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientList;
